import React, { useState } from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { find } from 'lodash';
import cn from 'classnames';
import { theme } from 'antd';

import { useStores } from '@/hooks/use-stores';
import EPModal from '@/comp/EPModal';
import EditIcon from '@/comp/common/icons/EditIcon';
import PlusCircleIcon from '@/comp/common/icons/PlusCircleIcon';
import ArrowUpIcon from '@/comp/common/icons/ArrowUpIcon';
import { EngineSubstepsEnum, FallbackBehaviour, DefaultBehaviour } from '@/store/Engine/constants';
import { FallbackRule } from '@/store/Engine/FallbackScenarios';
import ValidationTooltip from '@/comp/pages/EnginePage/Engine/Common/ValidationTooltip/ValidationTooltip';
import DeleteItemPopconfirmIcon from '@/comp/pages/EnginePage/Engine/Common/DeleteItemPopconfirmIcon/DeleteItemPopconfirmIcon';
import RulesGeneratorModal from '@/comp/pages/EnginePage/Engine/Common/RulesGeneratorModal/RulesGeneratorModal';
import { ValidationMessagePopoverGlobalStyle } from '@/comp/pages/EnginePage/Engine/Common/styled';
import { EPButton, EPSelect } from '@/components';

import { Card, ScenarioModalContentContainer } from './styled';
import Range from '../Common/Range/Range';
import { Radio } from '../Common/styled';
import FallbackRuleRadioGroup from './FallbackRuleRadioGroup';
import { sortSetpointsByElementIndex } from '@/store/Engine/utils';
import { RangeType } from '../Common/Range/Range.utils';

const { useToken } = theme;

const ScenarioModalContent = observer(({ onOkClick, onCancelClick, setHasTriedToSubmit, hasTriedToSubmit }) => {
  const { t } = useTranslation();
  const { engineStore, devicesStore, stationStore } = useStores();
  const { mainRangesConfiguration, fallbackScenarios, validationMessages } = engineStore;
  const [rulesGeneratorModalData, setRulesGeneratorModalData] = useState({
    open: false,
    data: undefined,
    scenario: undefined,
  });

  const onOk = () => {
    engineStore.clearValidationMessages();
    engineStore.validate(
      EngineSubstepsEnum.FallbackScenarios,
      undefined,
      undefined,
      stationStore.currentStation.devices,
    );
    if (!engineStore.validationMessages?.length) {
      onOkClick();
    }
  };

  const onCancel = () => {
    fallbackScenarios?.cancelChanges();
    onCancelClick();
  };

  const onAddScenarioClick = () => fallbackScenarios?.addScenario();

  const onScenarioFieldChange = (scenarioId, field, flowRangeKey?) => e => {
    fallbackScenarios.setScenarioField(scenarioId, field, e?.target?.value ?? e, flowRangeKey);
    if (field === 'fallbackBehaviour') {
      if (e?.target?.value === FallbackBehaviour.SingleRuleForAllFlowLevels)
        fallbackScenarios.setScenarioField(scenarioId, 'fallbackRules', [new FallbackRule()]);
      else if (e?.target?.value === FallbackBehaviour.LogicPerFlowLevel)
        fallbackScenarios.setScenarioField(
          scenarioId,
          'fallbackRules',
          mainRangesConfiguration?.flowLevels?.ranges?.map(
            range => new FallbackRule({ flowRangeKey: range?.key, flowRangeName: range?.name }),
          ),
        );
      else if (e?.target?.value === FallbackBehaviour.TurnOffEngine)
        fallbackScenarios.setScenarioField(scenarioId, 'fallbackRules', []);
    } else if (field === 'defaultBehaviour') {
      if (e?.target?.value === DefaultBehaviour.ExistingRule)
        fallbackScenarios.setScenarioField(
          scenarioId,
          'ruleParentKey',
          engineStore.mainRangesConfiguration?.pressureLevels?.ranges?.[0]?.key,
          flowRangeKey,
        );
      else fallbackScenarios.setScenarioField(scenarioId, 'ruleParentKey', undefined, flowRangeKey);
      if (e?.target?.value !== DefaultBehaviour.CustomRule)
        fallbackScenarios.setScenarioField(scenarioId, 'rules', undefined, flowRangeKey);
    }
  };

  const onDeleteScenarioClick = scenarioId => () => fallbackScenarios.deleteScenario(scenarioId);

  const onOpenRulesGeneratorModal = (fallbackRule, scenario) => () => {
    engineStore.clearValidationMessages();
    setRulesGeneratorModalData({ open: true, data: fallbackRule, scenario });
  };

  const onModalClose = () => {
    if (rulesGeneratorModalData.data?.hasRuleChanges()) rulesGeneratorModalData?.data?.cancelRuleChanges();
    setRulesGeneratorModalData({ open: false, data: undefined, scenario: undefined });
  };

  const onRuleGeneratorSaveClick = () => {
    rulesGeneratorModalData?.data?.setField(
      'rules',
      rulesGeneratorModalData?.data?.rules?.map(rule => ({ ...rule })),
    );
    setRulesGeneratorModalData({ open: false, data: undefined, scenario: undefined });
  };

  const onArrowUpClick = scenarioIndex => () => fallbackScenarios.moveScenario(scenarioIndex, scenarioIndex - 1);

  const onArrowDownClick = scenarioIndex => () => fallbackScenarios.moveScenario(scenarioIndex, scenarioIndex + 1);

  return (
    <ScenarioModalContentContainer>
      <span className="edit-scenarios-title">{t('editScenarios')}</span>
      <div className="scenarios">
        {fallbackScenarios?.scenarios?.map((scenario, index) => (
          <div className="scenario" key={scenario?.id}>
            <div className="scenario-name-container">
              <div className="scenario-name">
                <span className="scenario-name-label">{scenario?.name}</span>
                <DeleteItemPopconfirmIcon itemName={scenario?.name} onConfirm={onDeleteScenarioClick(scenario?.id)} />
              </div>
              {fallbackScenarios?.scenarios?.length > 1 ? (
                <div className="arrows">
                  {index > 0 && <ArrowUpIcon className="arrow" onClick={onArrowUpClick(index)} />}
                  {index < fallbackScenarios?.scenarios?.length - 1 && (
                    <ArrowUpIcon className={cn('arrow', 'transform-down')} onClick={onArrowDownClick(index)} />
                  )}
                </div>
              ) : null}
            </div>
            <div className="flex-row">
              <span className="on-failure-off-label">{t('onFailureOf')}</span>
              <ValidationTooltip
                message={find(validationMessages, { key: scenario?.id, field: 'compressors', focused: true })?.message}
              >
                <EPSelect
                  mode="multiple"
                  className="compressors-multiselect"
                  value={scenario?.compressors?.map(compressor => ({
                    value: compressor,
                    label: find(devicesStore.compressors, { id: compressor })?.name,
                  }))}
                  options={engineStore.sortedActiveCompressors?.map(compressor => ({
                    label: compressor?.name,
                    value: compressor?.id,
                  }))}
                  onChange={onScenarioFieldChange(scenario?.id, 'compressors')}
                />
              </ValidationTooltip>
            </div>
            <ValidationTooltip
              message={
                find(validationMessages, { key: scenario?.id, field: 'fallbackBehaviour', focused: true })?.message
              }
            >
              <Radio.Group
                value={scenario?.fallbackBehaviour}
                className="fallback-behaviour-radio-group"
                onChange={onScenarioFieldChange(scenario?.id, 'fallbackBehaviour')}
              >
                <Radio value={FallbackBehaviour.LogicPerFlowLevel}>{t('logicPerFlowLevel')}</Radio>
                {scenario?.fallbackBehaviour === FallbackBehaviour.LogicPerFlowLevel && (
                  <FallbackRuleRadioGroup
                    scenario={scenario}
                    onScenarioFieldChange={onScenarioFieldChange}
                    onRulesGeneratorClick={onOpenRulesGeneratorModal}
                  />
                )}
                <Radio value={FallbackBehaviour.SingleRuleForAllFlowLevels}>{t('singleRuleForAllFlowLevels')}</Radio>
                {scenario?.fallbackBehaviour === FallbackBehaviour.SingleRuleForAllFlowLevels && (
                  <FallbackRuleRadioGroup
                    scenario={scenario}
                    onScenarioFieldChange={onScenarioFieldChange}
                    onRulesGeneratorClick={onOpenRulesGeneratorModal}
                  />
                )}
                <Radio value={FallbackBehaviour.TurnOffEngine} disabled={true} className={'disabled'}>
                  {t('turnOffEngine')}
                </Radio>
              </Radio.Group>
            </ValidationTooltip>
          </div>
        ))}
      </div>
      <EPModal
        open={rulesGeneratorModalData?.open}
        onCancel={onModalClose}
        width="1032"
        height="600"
        centered
        closable={false}
        maskClosable={false}
        styles={{ body: { padding: 0 } }}
      >
        <RulesGeneratorModal
          rulesObject={rulesGeneratorModalData?.data}
          onOk={onRuleGeneratorSaveClick}
          disabledCompressors={rulesGeneratorModalData?.scenario?.compressors}
          hasTriedToSubmit={hasTriedToSubmit}
          setHasTriedToSubmit={setHasTriedToSubmit}
        />
      </EPModal>
      <EPButton size="medium" type="primary" onClick={onAddScenarioClick} className="add-scenario-button">
        <span className="button-label">{t('addScenario')}</span>
      </EPButton>
      <div className="bottom-buttons">
        <EPButton type="primary" onClick={onCancel}>
          <span className="cancel-button">{t('cancel')}</span>
        </EPButton>
        <EPButton type="primary" onClick={onOk}>
          <span className="button-label">{t('ok')}</span>
        </EPButton>
      </div>
      <ValidationMessagePopoverGlobalStyle />
    </ScenarioModalContentContainer>
  );
});

const CompressorsFallback = observer(({ setHasTriedToSubmit, hasTriedToSubmit }) => {
  const { engineStore } = useStores();
  const { t } = useTranslation();
  const {
    engineStore: {
      mainRangesConfiguration: { flowLevels, pressureLevels, unit },
      setpointsConfiguration,
      fallbackScenarios,
      isReadOnly,
    },
    devicesStore,
  } = useStores();
  const { token } = useToken();
  const [shouldDisplayModal, setShouldDisplayModal] = useState(false);

  const onOpenEditScenarioClick = () => setShouldDisplayModal(true);

  const onOkClick = () => setShouldDisplayModal(false);

  const onCancelClick = () => setShouldDisplayModal(false);

  return (
    <Card>
      <span className="card-title">
        {t('compressorsFallback')}
        {!isReadOnly && <PlusCircleIcon className="icon" onClick={onOpenEditScenarioClick} />}
      </span>
      <div className={cn('card-body', fallbackScenarios?.hasRulesByFlowLevel && 'fallback-by-flow-card-body')}>
        {fallbackScenarios?.scenarios?.map(scenario => (
          <div key={scenario?.id} className="card-item-container">
            <div className="card-item">
              <span className={cn('card-item-title', 'uppercase')}>
                {scenario?.name}
                {!isReadOnly && <EditIcon className="icon" onClick={onOpenEditScenarioClick} />}
              </span>
              <div className="compressors">
                <span className="on-failure-of-label">{t('onFailureOf')}</span>
                {scenario?.compressors?.map(compressor => (
                  <span className="chip-item" key={compressor}>
                    {find(devicesStore.compressors, { id: compressor })?.name ||
                      find(engineStore.pidChanges?.compressorsRemoved, { deviceId: compressor })?.deviceName}
                  </span>
                ))}
              </div>
              <div className="fallback-behaviour">
                {scenario?.fallbackBehaviour === FallbackBehaviour.TurnOffEngine ? (
                  <span className="turn-off-engine-label">{t('turnOffEngine')}</span>
                ) : (
                  scenario?.fallbackRules?.map((fallbackRule, index) => (
                    <div className="fallback-rules" key={fallbackRule?.flowRangeKey ?? index}>
                      {scenario?.fallbackBehaviour === FallbackBehaviour.LogicPerFlowLevel && (
                        <Range
                          fromFallbackOverview
                          unit={unit}
                          type={RangeType.Flow}
                          range={flowLevels.getRangeByKey(fallbackRule?.flowRangeKey)}
                        />
                      )}
                      <Range
                        fromFallbackOverview
                        unit={unit}
                        type={RangeType.Pressure}
                        range={
                          fallbackRule?.defaultBehaviour === DefaultBehaviour.ExistingRule
                            ? pressureLevels.getRangeByKey(fallbackRule?.ruleParentKey)
                            : { name: fallbackRule?.rules?.[0]?.name }
                        }
                        setpoints={
                          fallbackRule?.defaultBehaviour === DefaultBehaviour.CustomRule
                            ? sortSetpointsByElementIndex(fallbackRule?.rules, devicesStore?.sortedCompressors)
                            : setpointsConfiguration?.getSetpointsByPressureRange(fallbackRule?.ruleParentKey)
                        }
                      />
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      <EPModal
        open={!!shouldDisplayModal}
        centered
        width="680px"
        height="680px"
        cancelText={t('cancel')}
        closable={false}
        maskClosable={false}
        styles={{ body: { backgroundColor: token.colorBgContainer, padding: token.paddingXL } }}
      >
        <ScenarioModalContent
          onOkClick={onOkClick}
          onCancelClick={onCancelClick}
          setHasTriedToSubmit={setHasTriedToSubmit}
          hasTriedToSubmit={hasTriedToSubmit}
        />
      </EPModal>
    </Card>
  );
});

export default CompressorsFallback;
